import { Schema as S } from "effect";
import { defineMessageUnion } from "foldkit/message";
import { UrlRequest } from "foldkit/navigation";
import { Url } from "foldkit/url";

import { getFeaturesConfig } from "../api/commands/account";
import { getAttributeSchema } from "../api/commands/attributeSchema";
import { getEventActivity } from "../api/commands/events";
import { getLedgerBalance } from "../api/commands/ledger";
import { getTags } from "../api/commands/tags";
import { User } from "../auth/model";
import { PageMessage } from "../pages";
import * as LogInteraction from "../widgets/logInteraction";
import * as Sidebar from "../widgets/sidebar";
import type { Theme } from "./theme";

export const Message = defineMessageUnion({
	ClerkReady: {},
	ClickedLogInteraction: {},
	GotLogInteractionMessage: { message: LogInteraction.Message },
	GotSidebarMessage: { message: Sidebar.Message },
	ClerkReleased: {},
	FailedClerkLoad: { error: S.String },
	SessionChanged: { session: S.NullOr(User) },
	MountedClerkUi: {},
	ClickedLink: { request: UrlRequest },
	ChangedUrl: { url: Url },
	CompletedNavigate: {},
	CompletedTrackInteraction: {},
	ClickedOpenNavigation: {},
	ClickedCloseNavigation: {},
	ClickedAccountMenu: {},
	ClickedOrganizationSwitcher: {},
	CompletedFetchBalance: {
		generation: S.Number,
		result: getLedgerBalance.Result,
	},
	CompletedFetchFeatures: {
		generation: S.Number,
		result: getFeaturesConfig.Result,
	},
	CompletedFetchTags: { generation: S.Number, result: getTags.Result },
	CompletedFetchAttributeSchema: {
		generation: S.Number,
		result: getAttributeSchema.Result,
	},
	BalanceTick: {},
	BillingPreviewChanged: { generation: S.Number, enabled: S.Boolean },
	A2aContextChanged: { generation: S.Number, enabled: S.Boolean },
	ClickedDismissLimitNotice: {},
	ClickedThemeToggle: {},
	CompletedApplyTheme: {},
	CompletedInitTranslations: { ready: S.Boolean },
	CompletedIdentifyAnalytics: {},
	SelectedTimeTravelDate: { date: S.String },
	ClickedExitTimeTravel: {},
	/** The picker `<details>` opened (loads `month`'s heatmap) or closed. */
	ToggledTimeTravel: { isOpen: S.Boolean, month: S.String },
	/** Stepping a month inside the open picker. */
	ClickedTimeTravelMonth: { month: S.String },
	CompletedFetchActivity: {
		generation: S.Number,
		month: S.String,
		result: getEventActivity.Result,
	},
	ToggledIntegrations: { enabled: S.Boolean },
	/** Settings asked for the OS preference (v1's "system" theme). */
	RequestedSystemTheme: {},
	/** From the Tags page: tag data changed; the root re-fetches its tag suggestions. */
	TagsChanged: {},
	/** From Settings: the account is gone server-side; sign out and leave. */
	AccountDeleted: {},
	CompletedSignOut: {},
	CompletedPersistIntegrations: {},
	CompletedPersistImportDismissed: {},
	/** Cmd-K / Ctrl-K while Home or Records is active (R5). */
	PressedSearchShortcut: {},
	CompletedFocusSearch: {},
	GotPageMessage: { message: PageMessage },
});
export type Message = typeof Message.Type;

export type { Theme };
